import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";

/**
 * AdminAuthContext — admin-only JWT cookie session.
 * Distinct from MemberContext (which serves paid Hub members).
 *
 * States:
 *  - null  → checking
 *  - false → not authenticated as admin
 *  - object → admin user info
 */
const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
    const [admin, setAdmin] = useState(null);

    const refreshMe = async () => {
        try {
            const { data } = await api.get("/auth/me");
            if (data?.role === "admin") setAdmin(data);
            else setAdmin(false);
        } catch {
            setAdmin(false);
        }
    };

    useEffect(() => { refreshMe(); }, []);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data?.role !== "admin") {
                setAdmin(false);
                return { ok: false, error: "This sign-in is for administrators only." };
            }
            setAdmin(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        setAdmin(false);
    };

    return (
        <AdminAuthContext.Provider value={{ admin, login, logout, refreshMe }}>
            {children}
        </AdminAuthContext.Provider>
    );
}

export function useAdminAuth() {
    return useContext(AdminAuthContext);
}
